import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';

const CRITERIA_STATES = {
    PASS: 'pass',
    FAIL: 'fail',
    NA: 'na'
};

const AUTOFAIL_STATES = {
    PASS: 'pass',
    FAIL: 'fail'
};

function ScorecardEditModal({ isOpen, onClose, contact, scorecardData, config, onSave }) {
    const [formData, setFormData] = useState(null);
    const [showCloseWarning, setShowCloseWarning] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [originalFormData, setOriginalFormData] = useState(null);

    // Efficient change detection - only runs when called
    const hasChanges = useCallback(() => {
        if (!formData || !originalFormData) return false;
        return JSON.stringify(formData) !== JSON.stringify(originalFormData);
    }, [formData, originalFormData]);

    // Initialize form data when modal opens
    useEffect(() => {
        if (isOpen && scorecardData && config) {
            initializeFormData();
        }
    }, [isOpen, scorecardData, config]);

    const initializeFormData = () => {
        // Initialize criteria data - now working with raw structure
        const criteriaData = {};
        scorecardData.criteria.forEach(criteria => {
            const configCriteria = config.criteria.find(c => c.criteria_id === criteria.criteria_id);
            const isNullable = Boolean(configCriteria?.is_score_nullable);
            
            let state;
            if (criteria.score === null) {
                state = CRITERIA_STATES.NA; // null score = N/A
            } else if (criteria.is_failed) {
                state = CRITERIA_STATES.FAIL; // failed = No
            } else {
                state = CRITERIA_STATES.PASS; // passed = Yes
            }
            
            criteriaData[criteria.criteria_id] = {
                state: state,
                comment: criteria.genai_comment || '',
                failureReasons: criteria.failure_reason_ids || []
            };
        });
        
        // Initialize autofails data - now working with raw structure
        const autofailsData = {};
        config.autofails.forEach(autofail => {
            const existing = scorecardData.autofails?.find(af => af.autofail_id === autofail.autofail_id);
            autofailsData[autofail.autofail_id] = {
                state: existing ? (existing.is_failed ? AUTOFAIL_STATES.FAIL : AUTOFAIL_STATES.PASS) : AUTOFAIL_STATES.PASS,
                comment: existing?.genai_comment || ''
            };
        });

        const initialData = {
            agentWentAboveAndBeyond: scorecardData.agent_went_above_and_beyond || false,
            callerFrustrationBeginning: scorecardData.caller_frustration_beginning || false,
            criteria: criteriaData,
            autofails: autofailsData
        };

        setFormData(initialData);
        setOriginalFormData(JSON.parse(JSON.stringify(initialData))); // Deep copy for comparison
    };

    // Create a scoring-focused version of formData that only includes fields that affect scoring
    const scoringData = useMemo(() => {
        if (!formData) return null;
        
        // Extract only the state fields from criteria (not comments or failure reasons)
        const criteriaStates = {};
        Object.entries(formData.criteria).forEach(([criteriaId, data]) => {
            criteriaStates[criteriaId] = { state: data.state };
        });
        
        // Extract only the state fields from autofails (not comments)
        const autofailStates = {};
        Object.entries(formData.autofails).forEach(([autofailId, data]) => {
            autofailStates[autofailId] = { state: data.state };
        });
        
        return {
            agentWentAboveAndBeyond: formData.agentWentAboveAndBeyond,
            callerFrustrationBeginning: formData.callerFrustrationBeginning,
            criteria: criteriaStates,
            autofails: autofailStates
        };
    }, [formData]); // Simple dependency - ESLint will be happy

    // Calculate scores based on scoring-relevant data only
    const currentScores = useMemo(() => {
        if (!scoringData || !config) return null;
        
        // Check for autofails first - if any autofail is failed, entire score is 0%
        const hasAutofail = Object.values(scoringData.autofails).some(af => af.state === AUTOFAIL_STATES.FAIL);
        
        // Calculate section scores
        const sectionScores = config.sections.map(section => {
            const sectionCriteria = config.criteria.filter(c => c.section_id === section.section_id);
            
            let sectionMaxPoints = 0;
            let sectionEarnedPoints = 0;
            let applicableCriteria = 0;
            
            sectionCriteria.forEach(configCriteria => {
                const criteriaData = scoringData.criteria[configCriteria.criteria_id];
                if (criteriaData && criteriaData.state !== CRITERIA_STATES.NA) {
                    applicableCriteria++;
                    sectionMaxPoints += configCriteria.max_points;
                    if (criteriaData.state === CRITERIA_STATES.PASS) {
                        sectionEarnedPoints += configCriteria.max_points;
                    }
                }
            });
            
            const isNA = applicableCriteria === 0;
            const percentage = isNA ? null : Math.round((sectionEarnedPoints / sectionMaxPoints) * 100);
            
            return {
                section_id: section.section_id,
                name: section.label,
                score: sectionEarnedPoints,
                maxPoints: sectionMaxPoints,
                percentage: percentage,
                isNA: isNA
            };
        });
        
        // Calculate total score from all sections
        const totalMaxPoints = sectionScores.reduce((sum, section) => sum + (section.isNA ? 0 : section.maxPoints), 0);
        const totalEarnedPoints = sectionScores.reduce((sum, section) => sum + (section.isNA ? 0 : section.score), 0);
        
        const totalIsNA = totalMaxPoints === 0;
        let totalPercentage;
        
        if (hasAutofail) {
            totalPercentage = 0;
        } else if (totalIsNA) {
            totalPercentage = null; // N/A case
        } else {
            totalPercentage = Math.round((totalEarnedPoints / totalMaxPoints) * 100);
        }
        
        // Calculate individual criteria scores for display
        const criteriaScores = {};
        config.criteria.forEach(configCriteria => {
            const criteriaData = scoringData.criteria[configCriteria.criteria_id];
            if (criteriaData) {
                const isNA = criteriaData.state === CRITERIA_STATES.NA;
                const passed = criteriaData.state === CRITERIA_STATES.PASS;
                
                criteriaScores[configCriteria.criteria_id] = {
                    score: isNA ? null : (passed ? configCriteria.max_points : 0),
                    maxPoints: isNA ? null : configCriteria.max_points,
                    passed: passed,
                    isNA: isNA
                };
            }
        });
        
        return {
            total: {
                score: totalEarnedPoints,
                maxPoints: totalMaxPoints,
                percentage: totalPercentage,
                autofailed: hasAutofail,
                isNA: totalIsNA
            },
            sections: sectionScores,
            criteria: criteriaScores
        };
    }, [scoringData, config]); // Simple dependencies

    // Helper function to get score color
    const getScoreColor = (percentage) => {
        if (percentage === null) return "text-gray-500";
        if (percentage >= 80) return "text-green-600";
        if (percentage >= 60) return "text-yellow-600";
        return "text-red-600";
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: null
            }));
        }
    };

    const handleCriteriaChange = (criteriaId, field, value) => {
        setFormData(prev => ({
            ...prev,
            criteria: {
                ...prev.criteria,
                [criteriaId]: {
                    ...prev.criteria[criteriaId],
                    [field]: value
                }
            }
        }));
    };

    const handleAutofailChange = (autofailId, field, value) => {
        setFormData(prev => ({
            ...prev,
            autofails: {
                ...prev.autofails,
                [autofailId]: {
                    ...prev.autofails[autofailId],
                    [field]: value
                }
            }
        }));
    };

    const validateForm = () => {
        const errors = {};

        // Validate comments length
        Object.entries(formData.criteria).forEach(([criteriaId, data]) => {
            if (data.comment && data.comment.length > 400) {
                errors[`criteria_${criteriaId}_comment`] = 'Comment must be 400 characters or less';
            }
        });

        Object.entries(formData.autofails).forEach(([autofailId, data]) => {
            if (data.comment && data.comment.length > 400) {
                errors[`autofail_${autofailId}_comment`] = 'Comment must be 400 characters or less';
            }
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleClose = () => {
        if (hasChanges()) {
            setShowCloseWarning(true);
        } else {
            onClose();
        }
    };

    const confirmClose = () => {
        setShowCloseWarning(false);
        onClose();
    };

    const getFailureReasonsForCriteria = (criteriaId) => {
        return config.failureReasons.filter(fr => fr.criteria_id === criteriaId);
    };

    if (!isOpen || !formData) return null;

    const detectChanges = () => {
        const changes = {};
        
        // Check scorecard-level changes
        const scorecardChanges = {};
        
        if (formData.agentWentAboveAndBeyond !== originalFormData.agentWentAboveAndBeyond) {
            scorecardChanges.agent_went_above_and_beyond = formData.agentWentAboveAndBeyond ? 1 : 0;
        }
        
        if (formData.callerFrustrationBeginning !== originalFormData.callerFrustrationBeginning) {
            scorecardChanges.caller_frustration_beginning = formData.callerFrustrationBeginning ? 1 : 0;
        }
        
        if (Object.keys(scorecardChanges).length > 0) {
            changes.scorecard = scorecardChanges;
        }
        
        // Check criteria changes
        const criteriaChanges = {};
        Object.entries(formData.criteria).forEach(([criteriaId, formCriteria]) => {
            const originalCriteria = originalFormData.criteria[criteriaId];
            if (!originalCriteria) return;
            
            const hasStateChange = formCriteria.state !== originalCriteria.state;
            const hasCommentChange = formCriteria.comment !== originalCriteria.comment;
            const hasFailureReasonChange = JSON.stringify(formCriteria.failureReasons.sort()) !== 
                JSON.stringify(originalCriteria.failureReasons.sort());
            
            if (hasStateChange || hasCommentChange || hasFailureReasonChange) {
                criteriaChanges[criteriaId] = {
                    state: formCriteria.state,
                    comment: formCriteria.comment,
                    failure_reason_ids: formCriteria.failureReasons
                };
            }
        });
        
        if (Object.keys(criteriaChanges).length > 0) {
            changes.criteria = criteriaChanges;
        }
        
        // Check autofail changes
        const autofailChanges = {};
        Object.entries(formData.autofails).forEach(([autofailId, formAutofail]) => {
            const originalAutofail = originalFormData.autofails[autofailId];
            if (!originalAutofail) return;
            
            const hasStateChange = formAutofail.state !== originalAutofail.state;
            const hasCommentChange = formAutofail.comment !== originalAutofail.comment;
            
            if (hasStateChange || hasCommentChange) {
                autofailChanges[autofailId] = {
                    state: formAutofail.state,
                    comment: formAutofail.comment
                };
            }
        });
        
        if (Object.keys(autofailChanges).length > 0) {
            changes.autofails = autofailChanges;
        }
        
        return changes;
    };

    // Update the handleSave function
    const handleSave = () => {
        if (!validateForm()) return;
        
        const changes = detectChanges();
        
        if (Object.keys(changes).length === 0) {
            console.log("No changes detected");
            onClose();
            return;
        }
        
        // Call the save function with the detected changes
        onSave({
            contactId: contact.contact_id,
            changes: changes
        });
        
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-blue-600 text-white p-6 flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-white">Edit Scorecard</h2>
                    
                    <div className="flex items-center gap-6">
                        {currentScores && (
                            <div className="text-right">
                                <div className="text-2xl font-bold text-white">
                    Overall Score: {currentScores.total.autofailed ? (
                                        <span className="text-red-300">0% (AUTOFAILED)</span>
                                    ) : currentScores.total.isNA ? (
                                        <span className="text-gray-300">N/A</span>
                                    ) : (
                                        <span className={currentScores.total.percentage >= 80 ? 'text-green-300' : 
                                                    currentScores.total.percentage >= 60 ? 'text-yellow-300' : 'text-red-300'}>
                                            {currentScores.total.percentage}%
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        <button
                            onClick={handleClose}
                            className="text-white hover:text-gray-200 text-2xl"
                        >
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
                    {/* Overall Settings */}
                    <div className="mb-8 p-4 bg-gray-50 rounded-lg">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800">Overall Settings</h3>

                        {/* Checkboxes */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.agentWentAboveAndBeyond}
                                    onChange={(e) => handleInputChange('agentWentAboveAndBeyond', e.target.checked)}
                                    className="mr-2"
                                />
                                Agent Went Above & Beyond
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={formData.callerFrustrationBeginning}
                                    onChange={(e) => handleInputChange('callerFrustrationBeginning', e.target.checked)}
                                    className="mr-2"
                                />
                                Caller Indicated Initial Frustration
                            </label>
                        </div>
                    </div>

                    {/* Sections & Criteria */}
                    {config.sections.map(section => {
                        const sectionCriteria = config.criteria.filter(c => c.section_id === section.section_id);
                        
                        return (
                            <div key={section.section_id} className="mb-8 border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold mb-4 text-gray-800">{section.label}</h3>
                                    {currentScores && (
                                        <div className="text-right">
                                            {(() => {
                                                const sectionScore = currentScores.sections.find(s => s.section_id === section.section_id);
                                                if (!sectionScore) return null;
                                                
                                                if (sectionScore.isNA) {
                                                    return <div className="text-sm text-gray-500">N/A</div>;
                                                }
                                                
                                                return (
                                                    <div className={`text-base font-bold ${getScoreColor(sectionScore.percentage)}`}>
                                                        {sectionScore.score}/{sectionScore.maxPoints} = {sectionScore.percentage}%
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                                
                                {sectionCriteria.map(criteria => {
                                    const criteriaData = formData.criteria[criteria.criteria_id];
                                    const failureReasons = getFailureReasonsForCriteria(criteria.criteria_id);
                                    const isNullable = Boolean(criteria.is_score_nullable);
                                    const isFailing = criteriaData.state === 'fail';
                                    
                                    return (
                                        <div key={criteria.criteria_id} className={`mb-4 p-3 bg-gray-50 rounded ${isFailing ? 'border-l-4 border-red-400' : ''}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="font-medium text-gray-700">{criteria.label}</h4>
                                                {/* Dynamic Score Display */}
                                                {currentScores && (() => {
                                                    const criteriaScore = currentScores.criteria[criteria.criteria_id];
                                                    if (criteriaScore) {
                                                        if (criteriaScore.isNA) {
                                                            return (
                                                                <div className="text-gray-400 font-medium">
                                                                    N/A
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <div className={`font-bold ${criteriaScore.passed ? 'text-green-600' : 'text-red-600'}`}>
                                                                {criteriaScore.score}/{criteriaScore.maxPoints}
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                            {isFailing ? (
                                                // Expanded layout for failing criteria
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    {/* Left side - Radio buttons and failure reasons */}
                                                    <div>
                                                        {/* Radio Buttons */}
                                                        <div className="flex gap-6 mb-3">
                                                            <label className="flex items-center">
                                                                <input
                                                                    type="radio"
                                                                    name={`criteria_${criteria.criteria_id}`}
                                                                    value="pass"
                                                                    checked={criteriaData.state === 'pass'}
                                                                    onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'state', 'pass')}
                                                                    className="mr-2"
                                                                />
                                                                Yes
                                                            </label>
                                                            <label className="flex items-center">
                                                                <input
                                                                    type="radio"
                                                                    name={`criteria_${criteria.criteria_id}`}
                                                                    value="fail"
                                                                    checked={criteriaData.state === 'fail'}
                                                                    onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'state', 'fail')}
                                                                    className="mr-2"
                                                                />
                                                                No
                                                            </label>
                                                            {isNullable && (
                                                                <label className="flex items-center">
                                                                    <input
                                                                        type="radio"
                                                                        name={`criteria_${criteria.criteria_id}`}
                                                                        value="na"
                                                                        checked={criteriaData.state === 'na'}
                                                                        onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'state', 'na')}
                                                                        className="mr-2"
                                                                    />
                                                                    N/A
                                                                </label>
                                                            )}
                                                        </div>

                                                        {/* Failure Reasons */}
                                                        {failureReasons.length > 0 && (
                                                            <div>
                                                                <label className="block text-sm font-medium text-gray-600 mb-2">
                                                                    Failure Reasons
                                                                </label>
                                                                <div className="space-y-1">
                                                                    {failureReasons.map(reason => (
                                                                        <label key={reason.failure_reason_id} className="flex items-center">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={criteriaData.failureReasons.includes(reason.failure_reason_id)}
                                                                                onChange={(e) => {
                                                                                    const newReasons = e.target.checked
                                                                                        ? [...criteriaData.failureReasons, reason.failure_reason_id]
                                                                                        : criteriaData.failureReasons.filter(id => id !== reason.failure_reason_id);
                                                                                    handleCriteriaChange(criteria.criteria_id, 'failureReasons', newReasons);
                                                                                }}
                                                                                className="mr-2"
                                                                            />
                                                                            <span className="text-sm">{reason.label}</span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Right side - Comment (expanded) */}
                                                        <div>                                                           
                                                            <label className="block text-sm font-medium text-gray-600 mb-1">
                                                                Comment ({criteriaData.comment.length}/400)
                                                            </label>
                                                        <textarea
                                                            value={criteriaData.comment}
                                                            onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'comment', e.target.value)}
                                                            onInput={(e) => {
                                                                // Auto-resize textarea
                                                                e.target.style.height = 'auto';
                                                                e.target.style.height = Math.max(60, e.target.scrollHeight) + 'px';
                                                            }}
                                                            className={`w-full p-2 border rounded-md resize-none ${validationErrors[`criteria_${criteria.criteria_id}_comment`] ? 'border-red-500' : 'border-gray-300'}`}
                                                            rows={3}
                                                            style={{ minHeight: '60px' }}
                                                            maxLength={400}
                                                            placeholder="Explain the failure..."
                                                        />
                                                        {validationErrors[`criteria_${criteria.criteria_id}_comment`] && (
                                                            <p className="text-red-500 text-sm mt-1">{validationErrors[`criteria_${criteria.criteria_id}_comment`]}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : (
                                                // Compact layout for passing/N/A criteria
                                                <div className="flex items-center gap-4">
                                                    {/* Radio buttons on the left */}
                                                    <div className="flex gap-4">
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name={`criteria_${criteria.criteria_id}`}
                                                                value="pass"
                                                                checked={criteriaData.state === 'pass'}
                                                                onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'state', 'pass')}
                                                                className="mr-2"
                                                            />
                                                            Yes
                                                        </label>
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name={`criteria_${criteria.criteria_id}`}
                                                                value="fail"
                                                                checked={criteriaData.state === 'fail'}
                                                                onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'state', 'fail')}
                                                                className="mr-2"
                                                            />
                                                            No
                                                        </label>
                                                        {isNullable && (
                                                            <label className="flex items-center">
                                                                <input
                                                                    type="radio"
                                                                    name={`criteria_${criteria.criteria_id}`}
                                                                    value="na"
                                                                    checked={criteriaData.state === 'na'}
                                                                    onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'state', 'na')}
                                                                    className="mr-2"
                                                                />
                                                                N/A
                                                            </label>
                                                        )}
                                                    </div>
                                                    
                                                    {/* Compact comment box on the right */}
                                                    <div className="flex-1">
                                                        <textarea
                                                        value={criteriaData.comment}
                                                        onChange={(e) => handleCriteriaChange(criteria.criteria_id, 'comment', e.target.value)}
                                                        onInput={(e) => {
                                                            // Auto-resize textarea
                                                            e.target.style.height = 'auto';
                                                            e.target.style.height = Math.max(36, e.target.scrollHeight) + 'px';
                                                        }}
                                                        className={`w-full p-2 border rounded-md resize-none text-sm ${validationErrors[`criteria_${criteria.criteria_id}_comment`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        rows={1}
                                                        style={{ minHeight: '36px' }}
                                                        maxLength={400}
                                                        placeholder="Optional comment..."
                                                        />
                                                        {validationErrors[`criteria_${criteria.criteria_id}_comment`] && (
                                                            <p className="text-red-500 text-sm mt-1">{validationErrors[`criteria_${criteria.criteria_id}_comment`]}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}

                    {/* Autofails */}
                    <div className="mb-8 border rounded-lg p-4 bg-red-50 border-red-200">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center">
                            <FaExclamationTriangle className="text-red-500 mr-2" />
                            Autofail Elements
                        </h3>
                        
                        {config.autofails.map(autofail => {
                            const autofailData = formData.autofails[autofail.autofail_id];
                            const isFailing = autofailData.state === 'fail';
                            
                            return (
                                <div key={autofail.autofail_id} className={`mb-4 p-4 bg-white rounded border ${isFailing ? 'border-l-4 border-red-400' : ''}`}>
                                    <h4 className="font-medium text-gray-700 mb-3">{autofail.label}</h4>
                                    
                                    {/* Pass/Fail Toggle */}
                                    <div className="flex gap-6 mb-3">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name={`autofail_${autofail.autofail_id}`}
                                                value="fail"
                                                checked={autofailData.state === 'fail'}
                                                onChange={(e) => handleAutofailChange(autofail.autofail_id, 'state', 'fail')}
                                                className="mr-2"
                                            />
                                            Yes
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name={`autofail_${autofail.autofail_id}`}
                                                value="pass"
                                                checked={autofailData.state === 'pass'}
                                                onChange={(e) => handleAutofailChange(autofail.autofail_id, 'state', 'pass')}
                                                className="mr-2"
                                            />
                                            No
                                        </label>
                                    </div>

                                    {/* Comment - only show when failing */}
                                    {isFailing && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600 mb-1">
                                                Comment ({autofailData.comment.length}/400)
                                            </label>
                                            <textarea
                                                value={autofailData.comment}
                                                onChange={(e) => handleAutofailChange(autofail.autofail_id, 'comment', e.target.value)}
                                                onInput={(e) => {
                                                    // Auto-resize textarea
                                                    e.target.style.height = 'auto';
                                                    e.target.style.height = Math.max(60, e.target.scrollHeight) + 'px';
                                                }}
                                                className={`w-full p-2 border rounded-md resize-none ${validationErrors[`autofail_${autofail.autofail_id}_comment`] ? 'border-red-500' : 'border-gray-300'}`}
                                                rows={2}
                                                style={{ minHeight: '60px' }}
                                                maxLength={400}
                                                placeholder="Explain the autofail..."
                                            />
                                            {validationErrors[`autofail_${autofail.autofail_id}_comment`] && (
                                                <p className="text-red-500 text-sm mt-1">{validationErrors[`autofail_${autofail.autofail_id}_comment`]}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t p-6 flex justify-end gap-3">
                    <button
                        onClick={handleClose}
                        className="px-6 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        Save Changes
                    </button>
                </div>
            </div>

            {/* Close Warning Modal */}
            {showCloseWarning && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
                    <div className="bg-white rounded-lg p-6 max-w-md">
                        <h3 className="text-lg font-semibold mb-4">Unsaved Changes</h3>
                        <p className="text-gray-600 mb-6">
                            You have unsaved changes. Are you sure you want to close without saving?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowCloseWarning(false)}
                                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Keep Editing
                            </button>
                            <button
                                onClick={confirmClose}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                            >
                                Discard Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ScorecardEditModal;